import { Modal, Stack, Group, Text, Code, Badge, Table, Loader, Center, Alert, ScrollArea } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { getAuditLogDetail } from '../../services/auditLogApi'
import type { AuditLogDetail } from '../../types/auditLog'
import { operationColor } from './auditFormat'

interface AuditDetailModalProps {
  logId: string | null
  opened: boolean
  onClose: () => void
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * One recorded value. A nested object or array — a recipient list, a before-and-after pair inside a governance row's
 * detail — is shown as indented JSON, since on one line it is a wall; anything else is a single line, as it always was.
 */
function ValueCell({ value, color }: { value: unknown; color?: string }) {
  if (value !== null && typeof value === 'object') {
    return <Code block c={color}>{JSON.stringify(value, null, 2)}</Code>
  }
  return <Code c={color}>{formatValue(value)}</Code>
}

function ValueTable({ values }: { values: Record<string, unknown> }) {
  const entries = Object.entries(values)
  if (entries.length === 0) {
    return <Text size="sm" c="dimmed">No values recorded.</Text>
  }
  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Property</Table.Th>
          <Table.Th>Value</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {entries.map(([key, value]) => (
          <Table.Tr key={key}>
            <Table.Td>{key}</Table.Td>
            <Table.Td><ValueCell value={value} /></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

function ValueDiffTable({
  oldValues,
  newValues,
  changedProperties,
}: {
  oldValues: Record<string, unknown>
  newValues: Record<string, unknown>
  changedProperties: string[]
}) {
  // Fall back to the union of keys if no explicit changed list was recorded.
  const columns = changedProperties.length > 0
    ? changedProperties
    : Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]))

  if (columns.length === 0) {
    return <Text size="sm" c="dimmed">No field changes recorded.</Text>
  }

  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Property</Table.Th>
          <Table.Th>Old Value</Table.Th>
          <Table.Th>New Value</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {columns.map((col) => (
          <Table.Tr key={col}>
            <Table.Td>{col}</Table.Td>
            <Table.Td><ValueCell value={oldValues[col]} color="red" /></Table.Td>
            <Table.Td><ValueCell value={newValues[col]} color="green" /></Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  )
}

/**
 * What an entry recorded, by its operation.
 *
 * **Create, Update and Delete** are the audit interceptor's rows: the entity's values after, before and after, and
 * before. **Every other operation** is Cartographer's governance auditor — a grant, a schedule or workspace change, a
 * report edit, a request, a retirement, an Atlas edit — which writes through another path: its detail JSON is stored as
 * the entry's new values, and an Atlas edit carries before and after. Those rows used to show nothing below the header.
 * They show whichever values the entry carries — before and after as a diff, one side as a table — and, when it carries
 * neither, a line saying so, rather than a blank that reads as a failure to load.
 */
function AuditValues({ log }: { log: AuditLogDetail }) {
  switch (log.operation) {
    case 'Update':
      return (
        <ValueDiffTable
          oldValues={log.oldValues}
          newValues={log.newValues}
          changedProperties={log.changedProperties}
        />
      )
    case 'Create':
      return <ValueTable values={log.newValues} />
    case 'Delete':
      return <ValueTable values={log.oldValues} />
    default: {
      const hasOld = Object.keys(log.oldValues ?? {}).length > 0
      const hasNew = Object.keys(log.newValues ?? {}).length > 0

      if (hasOld && hasNew) {
        return (
          <ValueDiffTable
            oldValues={log.oldValues}
            newValues={log.newValues}
            changedProperties={log.changedProperties ?? []}
          />
        )
      }
      if (hasNew) return <ValueTable values={log.newValues} />
      if (hasOld) return <ValueTable values={log.oldValues} />
      return <Text size="sm" c="dimmed">No details were recorded for this operation.</Text>
    }
  }
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text fw={500} size="sm" w={120} style={{ flexShrink: 0 }}>{label}</Text>
      <div>{children}</div>
    </Group>
  )
}

export function AuditDetailModal({ logId, opened, onClose }: AuditDetailModalProps) {
  const { data: log, isLoading, error } = useQuery({
    queryKey: ['audit-detail', logId],
    queryFn: () => getAuditLogDetail(logId as string),
    enabled: opened && !!logId,
  })

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title="Audit Entry Details">
      {isLoading && (
        <Center py="xl"><Loader /></Center>
      )}

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Failed to load">
          {error instanceof Error ? error.message : 'Could not load audit entry.'}
        </Alert>
      )}

      {log && (
        <Stack gap="sm">
          <DetailRow label="Entity Type">{log.entityType}</DetailRow>
          <DetailRow label="Entity Id"><Code>{log.entityId}</Code></DetailRow>
          <DetailRow label="Operation">
            <Badge color={operationColor(log.operation)}>{log.operation}</Badge>
          </DetailRow>
          <DetailRow label="User">{log.userName || log.userId || '—'}</DetailRow>
          <DetailRow label="IP Address">{log.clientIpAddress || '—'}</DetailRow>
          <DetailRow label="Timestamp">{new Date(log.timestamp).toLocaleString()}</DetailRow>
          <DetailRow label="Source">{log.source || '—'}</DetailRow>
          {log.correlationId && (
            <DetailRow label="Correlation"><Code>{log.correlationId}</Code></DetailRow>
          )}

          <ScrollArea.Autosize mah={400} mt="sm">
            <AuditValues log={log} />
          </ScrollArea.Autosize>
        </Stack>
      )}
    </Modal>
  )
}
